import type { ApiClient } from "@src/packages/api/louise-api";
import {
  ApiRequestError,
  fetchApiResponse,
  throwApiError,
} from "@src/packages/api/api-error";
import type {
  Clinic,
  UserClinicInteraction,
  ClinicsFilters,
} from "./clinics.types";
import type {
  PaginatedResponse,
  PaginationParams,
} from "@src/packages/pagination/pagination.types";
import {
  transformCmsClinicToClinic,
  buildQueryParams,
  type CmsClinicsResponse,
  type CmsClinicResponse,
} from "./clinics.mapper";
import { getCmsApiBaseUrl } from "./utils/apiUtils";
import { applyClientSideFilters } from "./utils/filterUtils";

// ============================================
// CMS Clinic Data (public, no auth)
// ============================================

export async function getClinics(
  client: ApiClient,
  filters?: ClinicsFilters,
  pagination: PaginationParams = { page: 1, limit: 12 },
): Promise<PaginatedResponse<Clinic>> {
  const baseUrl = getCmsApiBaseUrl();
  const queryString = buildQueryParams(filters, pagination);
  const url = `${baseUrl}/clinics${queryString ? `?${queryString}` : ""}`;

  try {
    const response = await fetchApiResponse(
      url,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
      "Failed to reach the clinics service",
      "getClinics",
    );

    if (!response.ok) {
      const status = response.status;
      const statusText = response.statusText;
      throw new ApiRequestError(
        `Failed to fetch clinics: ${status} ${statusText || "unknown error"}`,
        { status, context: "getClinics" },
      );
    }

    const cmsData = (await response.json()) as CmsClinicsResponse;
    let clinics = cmsData.docs.map(transformCmsClinicToClinic);

    if (filters) {
      clinics = applyClientSideFilters(clinics, filters);
    }

    return {
      data: clinics,
      pagination: {
        currentPage: cmsData.page,
        totalPages: cmsData.totalPages,
        totalItems: cmsData.totalDocs,
        itemsPerPage: cmsData.limit,
        hasNextPage: cmsData.hasNextPage,
        hasPreviousPage: cmsData.hasPrevPage,
      },
    };
  } catch (error) {
    console.error("Error fetching clinics from CMS API:", {
      error: error instanceof Error ? error.message : String(error),
      filters,
      pagination,
    });
    throw error;
  }
}

export async function getClinicById(
  client: ApiClient,
  clinicId: string,
): Promise<Clinic | null> {
  const baseUrl = getCmsApiBaseUrl();
  const url = `${baseUrl}/clinics/${clinicId}`;

  try {
    const response = await fetchApiResponse(
      url,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
      "Failed to reach the clinics service",
      "getClinicById",
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const status = response.status;
      const statusText = response.statusText;
      throw new ApiRequestError(
        `Failed to fetch clinic: ${status} ${statusText || "unknown error"}`,
        { status, context: "getClinicById" },
      );
    }

    const cmsClinic = (await response.json()) as CmsClinicResponse;
    return transformCmsClinicToClinic(cmsClinic);
  } catch (error) {
    console.error("Error fetching clinic from CMS API:", {
      clinicId,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
}

export async function createClinic(
  client: ApiClient,
  clinicData: Omit<Clinic, "id">,
): Promise<Clinic> {
  throw new Error("createClinic not yet implemented with CMS API");
}

export async function updateClinic(
  client: ApiClient,
  clinicId: string,
  updates: Partial<Omit<Clinic, "id">>,
): Promise<Clinic | null> {
  throw new Error("updateClinic not yet implemented with CMS API");
}

export async function deleteClinic(
  client: ApiClient,
  clinicId: string,
): Promise<boolean> {
  throw new Error("deleteClinic not yet implemented with CMS API");
}

// ============================================
// User Clinic Interactions (dashboard API)
// ============================================

export async function getUserClinicInteraction(
  client: ApiClient,
  tenantId: string,
  clinicId: string,
): Promise<UserClinicInteraction | null> {
  if (!tenantId) return null;

  const { data, error, response } = await client.GET(
    "/me/provider-interactions/{providerId}",
    {
      params: { path: { providerId: clinicId } },
    },
  );

  if (error) {
    if (response?.status === 404) return null;
    throwApiError(
      error,
      "Failed to load clinic interaction",
      "getUserClinicInteraction",
      response?.status,
    );
  }

  const d = data.data;
  return {
    id: String(d.id ?? ""),
    userId: String(d.userId ?? ""),
    clinicId: d.providerId,
    isLiked: d.isLiked,
    notes: d.notes,
    createdAt: String(d.createdAt ?? ""),
    updatedAt: String(d.updatedAt ?? ""),
  };
}

export async function listUserClinicInteractions(
  client: ApiClient,
  tenantId: string,
): Promise<UserClinicInteraction[]> {
  if (!tenantId) return [];

  const { data, error, response } = await client.GET(
    "/me/provider-interactions",
  );

  if (error) {
    throwApiError(
      error,
      "Failed to load clinic interactions",
      "listUserClinicInteractions",
      response?.status,
    );
  }

  return (data.data ?? []).map((d) => ({
    id: String(d.id ?? ""),
    userId: String(d.userId ?? ""),
    clinicId: d.providerId,
    isLiked: d.isLiked,
    notes: d.notes,
    createdAt: String(d.createdAt ?? ""),
    updatedAt: String(d.updatedAt ?? ""),
  }));
}

export async function upsertUserClinicInteraction(
  client: ApiClient,
  tenantId: string,
  clinicId: string,
  interactionData: { isLiked?: boolean; notes?: string | null },
): Promise<UserClinicInteraction> {
  if (!tenantId) {
    throw new Error("No tenant ID");
  }

  const { data, error, response } = await client.PUT(
    "/me/provider-interactions/{providerId}",
    {
      params: { path: { providerId: clinicId } },
      body: interactionData,
    },
  );

  if (error) {
    throwApiError(
      error,
      "Failed to save clinic interaction",
      "upsertUserClinicInteraction",
      response?.status,
    );
  }

  const d = data.data;
  return {
    id: String(d.id ?? ""),
    userId: String(d.userId ?? ""),
    clinicId: d.providerId,
    isLiked: d.isLiked,
    notes: d.notes,
    createdAt: String(d.createdAt ?? ""),
    updatedAt: String(d.updatedAt ?? ""),
  };
}

export async function deleteUserClinicInteraction(
  client: ApiClient,
  tenantId: string,
  clinicId: string,
): Promise<boolean> {
  if (!tenantId) return false;

  const { error, response } = await client.DELETE(
    "/me/provider-interactions/{providerId}",
    {
      params: { path: { providerId: clinicId } },
    },
  );

  if (error) {
    throwApiError(
      error,
      "Failed to delete clinic interaction",
      "deleteUserClinicInteraction",
      response?.status,
    );
  }
  return true;
}

// Legacy aliases
export const createUserClinicInteraction = upsertUserClinicInteraction;
export const updateUserClinicInteraction = upsertUserClinicInteraction;
